import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight } from 'lucide-react';
import { useCartStore } from '@/stores/cart-store';
import { formatCurrency } from '@/lib/utils';
import Button from '@/components/ui/Button';
import FreeShippingBar from '@/components/upsell/FreeShippingBar';
import RecentlyViewed from '@/components/upsell/RecentlyViewed';

export default function Cart() {
  const { t } = useTranslation('cart');
  const { items, couponCode, couponDiscount, couponRemovedReason, isLoading, fetchCart, updateQuantity, removeItem, subtotal, bundleDiscount, total, applyCoupon, removeCoupon } = useCartStore();
  const [couponError, setCouponError] = useState<string | null>(null);

  useEffect(() => { fetchCart(); }, [fetchCart]);

  if (items.length === 0 && !isLoading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-16 text-center">
        <ShoppingBag className="h-16 w-16 text-ink-muted mx-auto mb-4" aria-hidden="true" />
        <h1 className="text-2xl font-display font-bold text-ink mb-2">{t('empty.heading')}</h1>
        <p className="text-ink-muted mb-6">{t('empty.description')}</p>
        <Link to="/shop">
          <Button>{t('empty.cta')}</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-2xl font-display font-bold text-ink mb-6">{t('title')}</h1>

      <FreeShippingBar />

      <div className="flex flex-col lg:flex-row gap-8">
        {/* Items */}
        <div className="flex-1">
          <div className="space-y-4">
            {items.map((item) => (
              <article key={item.id} className="flex gap-4 p-4 bg-surface rounded-lg border border-line">
                {item.image_url ? (
                  <img src={item.image_url} alt={item.product_name} className="w-20 h-20 sm:w-24 sm:h-24 object-cover rounded-md" />
                ) : (
                  <div className="w-20 h-20 sm:w-24 sm:h-24 bg-surface-muted rounded-md flex items-center justify-center">
                    <ShoppingBag className="h-8 w-8 text-ink-muted" aria-hidden="true" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <h2 className="font-medium text-ink truncate">{item.product_name}</h2>
                  <p className="text-sm text-ink-muted">{item.variant_name}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <p className="text-sm font-semibold text-sage-700">{formatCurrency(item.price)}</p>
                    {item.bundle_name && (
                      <span className="text-xs bg-sage-50 text-sage-700 px-1.5 py-0.5 rounded font-medium">
                        {item.bundle_name}
                      </span>
                    )}
                  </div>
                  {item.bundle_discount > 0 && (
                    <p className="text-xs text-success-600 mt-0.5">
                      {t('item.bundleDiscount', { amount: formatCurrency(item.bundle_discount) })}
                    </p>
                  )}
                </div>
                <div className="flex flex-col items-end gap-2">
                  <div className="flex items-center border border-line-strong rounded-lg">
                    <button
                      onClick={() => item.quantity > 1 ? updateQuantity(item.id, item.quantity - 1) : removeItem(item.id)}
                      className="p-2 hover:bg-surface-soft min-w-[44px] min-h-[44px] flex items-center justify-center"
                      aria-label={t('item.decrementAriaLabel', { name: item.product_name })}
                    >
                      <Minus className="h-4 w-4" />
                    </button>
                    <span className="px-3 text-sm font-medium min-w-[2rem] text-center" aria-label={t('item.quantityAriaLabel', { count: item.quantity })}>
                      {item.quantity}
                    </span>
                    <button
                      onClick={() => updateQuantity(item.id, item.quantity + 1)}
                      className="p-2 hover:bg-surface-soft min-w-[44px] min-h-[44px] flex items-center justify-center"
                      aria-label={t('item.incrementAriaLabel', { name: item.product_name })}
                      disabled={item.quantity >= item.max_quantity}
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                  <button
                    onClick={() => removeItem(item.id)}
                    className="text-red-500 hover:text-red-700 p-1 min-w-[44px] min-h-[44px] flex items-center justify-center"
                    aria-label={t('item.removeAriaLabel', { name: item.product_name })}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </article>
            ))}
          </div>
        </div>

        {/* Summary */}
        <aside className="lg:w-80" aria-label={t('summary.ariaLabel')}>
          <div className="bg-surface rounded-lg border border-line p-6 sticky top-24">
            <h2 className="text-lg font-semibold text-ink mb-4">{t('summary.heading')}</h2>

            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-ink-soft">{t('summary.subtotal')}</span>
                <span className="font-medium">{formatCurrency(subtotal())}</span>
              </div>
              {bundleDiscount() > 0 && (
                <div className="flex justify-between text-success-700">
                  <span>{t('summary.bundleSavings')}</span>
                  <span>-{formatCurrency(bundleDiscount())}</span>
                </div>
              )}
              {couponCode && (
                <div className="flex justify-between text-success-700">
                  <span>{t('summary.discountWithCode', { code: couponCode })}</span>
                  <span>-{formatCurrency(couponDiscount)}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-ink-soft">{t('summary.shipping')}</span>
                <span className="text-ink-muted">{t('summary.shippingCalculated')}</span>
              </div>
            </div>

            <div className="border-t border-line mt-4 pt-4 flex justify-between text-base font-semibold">
              <span>{t('summary.total')}</span>
              <span>{formatCurrency(total())}</span>
            </div>

            {/* Coupon */}
            {!couponCode && (
              <form
                className="mt-4 flex gap-2"
                onSubmit={async (e) => {
                  e.preventDefault();
                  const input = (e.target as HTMLFormElement).elements.namedItem('coupon') as HTMLInputElement;
                  if (!input.value.trim()) return;
                  // The store rethrows a rejected code; without this catch the
                  // rejection was unhandled and the shopper saw nothing.
                  try {
                    setCouponError(null);
                    await applyCoupon(input.value);
                    input.value = '';
                  } catch (err) {
                    setCouponError((err as Error)?.message || t('promo.invalid'));
                  }
                }}
              >
                <label htmlFor="coupon-input" className="sr-only">{t('promo.label')}</label>
                <input
                  id="coupon-input"
                  name="coupon"
                  type="text"
                  placeholder={t('promo.placeholder')}
                  className="flex-1 px-3 py-2 border border-line-strong rounded-lg text-sm"
                />
                <Button variant="outline" size="sm" type="submit">{t('promo.apply')}</Button>
              </form>
            )}
            {(couponError || (!couponCode && couponRemovedReason)) && (
              <p className="mt-2 text-sm text-red-600" role="alert">
                {couponError ?? t('promo.invalid')}
              </p>
            )}
            {couponCode && (
              <button onClick={removeCoupon} className="mt-2 text-sm text-red-500 hover:text-red-700">
                {t('promo.remove')}
              </button>
            )}

            <Link to="/checkout" className="block mt-6">
              <Button className="w-full" size="lg">
                {t('checkoutCta')}
                <ArrowRight className="h-4 w-4 ml-2" aria-hidden="true" />
              </Button>
            </Link>

            <Link to="/shop" className="block mt-3 text-center text-sm text-sage-700 hover:text-sage-800">
              {t('continueShopping')}
            </Link>
          </div>
        </aside>
      </div>

      <RecentlyViewed />
    </div>
  );
}
